import {FlexRow, Modal, TextField} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../shared/components/modals/types';
import Util from '../../../shared/util/util';
import CreateProjectStageMutation from '../../../../mutations/CreateProjectStageMutation';
import RenameProjectStageMutation from '../../../../mutations/RenameProjectStageMutation';
import {MAX_STAGE_NAME_LENGTH, StageNameError, stageNameErrorFromServer, validateStageName} from '../projectStageValidation';

export type ProjectStageModalPayload = {
	/** The company's Relay id; a new stage is appended to its projectStages connection. */
	companyId: string;
	/** The custom stage being edited. Absent when adding a stage. */
	stage?: {id: string; name: string};
	/** Every other stage's stored name, defaults included, for the duplicate check. */
	otherNames: ReadonlyArray<string>;
};

export const showProjectStageModal = (props: ProjectStageModalPayload): void => {
	showModal({
		type: MODAL_TYPE.PROJECT_STAGE,
		...props,
	});
};

const ERROR_MESSAGE_IDS: Record<StageNameError, string> = {
	required: 'settings.project_stages.error.name_required',
	duplicate: 'settings.project_stages.error.name_duplicate',
};

/** Adds a custom project stage, or edits the name of one. Saving happens here, not on the page. */
export const ProjectStageModal = ({
	closeModal,
	companyId,
	stage,
	otherNames,
}: ModalProps<ProjectStageModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [name, setName] = useState(stage?.name ?? '');
	const [touched, setTouched] = useState(false);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState<StageNameError | null>(null);

	// A save outlives the modal if it is cancelled or dismissed mid-flight. The conductor's closeModal
	// pops whichever modal is on top, so a late success must not call it for a modal that is already gone.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const localError = validateStageName(name, otherNames);
	// "Required" is only worth saying once the admin has typed and cleared the field; an untouched empty
	// field just keeps Save disabled.
	const shownError = serverError ?? (localError === 'required' && !touched ? null : localError);

	const handleChange = (value: string) => {
		setName(value);
		setTouched(true);
		setServerError(null);
	};

	// Saving only ends on a path that really ends it: the default handler returns true when it has
	// scheduled a retry, and Save must stay disabled until that attempt settles.
	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		if (!mounted.current) {
			onFailure(error);
			return;
		}
		const nameError = stageNameErrorFromServer(error);
		if (nameError) {
			setServerError(nameError);
			setSaving(false);
			return;
		}
		if (onFailure(error) !== true) {
			setSaving(false);
		}
	};

	const handleSaved = () => {
		if (mounted.current) {
			closeModal();
		}
	};

	const handleSave = () => {
		if (localError || saving) {
			return;
		}
		setSaving(true);
		const trimmed = name.trim();
		if (stage) {
			Util.CommitMutation(
				RenameProjectStageMutation,
				{id: stage.id, name: trimmed},
				handleSaved,
				true,
				null,
				handleError
			);
		} else {
			// Creating is not idempotent: if the first request committed but its response was lost, an
			// automatic retry would be refused as a duplicate. Renaming to the same name twice is harmless.
			Util.CommitMutation(CreateProjectStageMutation, {companyId, name: trimmed}, handleSaved, false, null, handleError);
		}
	};

	const title = intl.formatMessage({id: stage ? 'settings.project_stages.edit' : 'settings.project_stages.add'});

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				<TextField
					label={intl.formatMessage({id: 'settings.project_stages.name_label'})}
					value={name}
					onValueChange={handleChange}
					maxLength={MAX_STAGE_NAME_LENGTH}
					errorText={shownError ? intl.formatMessage({id: ERROR_MESSAGE_IDS[shownError]}) : ''}
					data-cy="project-stage-name-field"
				/>
				<FlexRow>
					<Modal.PrimaryFooterButton
						onPress={handleSave}
						disabled={!!localError || saving}
						dataCy="project-stage-save"
					>
						{intl.formatMessage({id: 'common.save'})}
					</Modal.PrimaryFooterButton>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						{intl.formatMessage({id: 'common.cancel'})}
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
